import type { ComponentType } from 'react';

import { BomiNewsWidget } from '@/pages/home/widgets/BomiNewsWidget';
import { EmployeeNewsWidget } from '@/pages/home/widgets/EmployeeNewsWidget';
import { HrInfoWidget } from '@/pages/home/widgets/HrInfoWidget';
import { MarketWidget } from '@/pages/home/widgets/MarketWidget';
import { MeetingsWeekWidget } from '@/pages/home/widgets/MeetingsWeekWidget';
import { NewsWidget } from '@/pages/home/widgets/NewsWidget';
import { NoticesWidget } from '@/pages/home/widgets/NoticesWidget';
import { OfficeSummaryWidget } from '@/pages/home/widgets/OfficeSummaryWidget';
import { ImprovementRequestsWidget } from '@/pages/home/widgets/ImprovementRequestsWidget';
import { SiteChatRoomsWidget } from '@/pages/home/widgets/SiteChatRoomsWidget';
import { SiteListWidget } from '@/pages/home/widgets/SiteListWidget';
import { SitePrepWidget } from '@/pages/home/widgets/SitePrepWidget';
import { SiteSummaryWidget } from '@/pages/home/widgets/SiteSummaryWidget';
import { TechEducationWidget } from '@/pages/home/widgets/TechEducationWidget';
import { TodoWidget } from '@/pages/home/widgets/TodoWidget';
import { WeatherWidget } from '@/pages/home/widgets/WeatherWidget';
import { WeeklyMenuWidget } from '@/pages/home/widgets/WeeklyMenuWidget';
import { WeeklyReportWidget } from '@/pages/home/widgets/WeeklyReportWidget';

/**
 * 홈에 놓을 수 있는 위젯 목록. <b>여기가 유일한 정의</b>다.
 *
 * 메뉴가 `menuConfig.ts` 한 곳에서 나오는 것과 같은 이유다 — 위젯 목록과 위젯 관리 모달의 목록을
 * 따로 적으면, 위젯을 하나 더했을 때 반드시 한쪽만 갱신되는 날이 온다.
 *
 * ## 노출 규칙
 *
 * `requiresMenuKey` 는 <b>인가가 아니다.</b> 카탈로그에서 감추는 것은 UX 이고, 위젯이 실제로 쓰는
 * 데이터는 각 API 가 요청마다 다시 판정한다. 이 값을 전부 지워도 데이터가 새어서는 안 된다.
 *
 * `fixed` 는 사용자가 끌 수 없는 위젯이다. <b>지금 이 값을 쓰는 위젯은 하나도 없다</b>
 * (사용자 지시 2026-09-23 — 할일·공지의 고정을 풀었다). 값과 정렬 규칙(`rankOf`)은 남겨 둔다:
 * 다시 고정할 위젯이 생겼을 때 되살릴 자리가 여기여야 하고, 서버가 세우는 조건 고정(`forced`)은
 * 그 자리를 그대로 쓰고 있다.
 *
 * `onlyWhenEligible` 은 <b>서버가 조건을 세웠을 때만</b> 카탈로그에 들어온다
 * (`GET /v1/home/widgets/conditions` 의 `eligible`). 조건이 없으면 위젯 관리에도 나오지 않는다 —
 * 켜 봐야 늘 빈 칸인 위젯을 목록에 남겨 두지 않는다. 그중 「저절로 켜지기까지」 하는 것은 같은
 * 응답의 `forced` 가 따로 정한다(착공 준비).
 */

export type WidgetSize = 'sm' | 'wide';

export type WidgetDefinition = {
  widgetKey: string;
  title: string;
  /** 위젯 관리 모달에서 무엇을 보여주는 위젯인지 설명한다. */
  description: string;
  icon: string;
  fixed: boolean;
  sizes: WidgetSize[];
  defaultSize: WidgetSize;
  /** 아직 손대지 않은 사용자에게 처음부터 보일지. */
  defaultVisible: boolean;
  /**
   * 이 메뉴를 볼 수 없는 계정에게는 카탈로그에도 내놓지 않는다.
   *
   * <p><b>여럿을 적으면 「하나라도 있으면」</b> 이다(2026-09-18 · 사이드바 `visibleWhenAnyOf` 와 같은 뜻).
   * 위젯 하나가 자료 둘을 토글로 가르는 경우가 그것이다 — 뉴스·건설동향은 권한 키가 따로인데,
   * 한쪽만 가진 사람에게 위젯을 통째로 감추면 <b>볼 수 있는 쪽까지 못 본다.</b> 못 보는 쪽은
   * 눌렀을 때 서버가 답한다(위젯 칸에 「볼 수 있는 권한이 없습니다」).
   */
  requiresMenuKey?: string | string[];
  /**
   * 서버가 조건을 세운 동안에만 카탈로그에 내놓는다(`GET /v1/home/widgets/conditions` 의 `eligible`).
   *
   * 해당 없는 사람에게는 <b>내용이 영영 없는</b> 위젯인 경우다. 조건이 없을 때 위젯 관리 목록에
   * 남겨 두면, 켜 봐야 늘 빈 칸인 위젯을 사람이 골라 넣게 된다 — 그리고 정작 조건이 섰을 때
   * 그 사람은 「내가 켜 둔 빈 칸」 으로 기억해 읽지 않는다.
   */
  onlyWhenEligible?: boolean;
  /**
   * 격자가 아닌 <b>홈 오른쪽 세로 기둥</b>에 선다(사용자 지시 2026-10-15 — 「우측 사이드바처럼」).
   * `placeSlots` 가 격자에서 빼고 `HomePage` 가 따로 세운다. 크기·좌표는 쓰지 않는다.
   */
  placement?: 'rail';
  Component: ComponentType;
};

/*
  ★ **순서가 곧 기본 배치다.** 저장해 둔 것이 없는 사람(새 계정 · 「기본으로 되돌리기」)의 홈은
  이 배열 차례대로 선다(`mergeSlots` 의 `added`).

  <p>할일·공지를 <b>맨 앞에 둔다</b>. 2026-09-23 에 둘의 고정(`fixed`)을 풀었는데, 고정이
  자리까지 정하고 있었다(`rankOf` 의 0 층) — 풀기만 하면 매일 여는 두 위젯이 인사 정보·현장 준비
  아래로 내려간다. 고정을 푼 것은 <b>끌 수 있게</b> 하려던 것이지 자리를 내리려던 것이 아니라,
  자리는 이 순서로 지킨다.

  <p>이미 배치를 저장한 사람은 이 순서와 무관하다 — 저장된 차례가 그대로 남는다.
*/
export const WIDGET_DEFINITIONS: WidgetDefinition[] = [
  {
    widgetKey: 'todo',
    title: 'TO DO 할일',
    description: '직접 적은 할일과, 처리해야 할 일(주간보고 미작성 등)을 함께 보여줍니다.',
    icon: 'check',
    // 고정을 풀었다(사용자 지시 2026-09-23). 기본은 여전히 켜져 있고(`defaultVisible`),
    // 끄고 싶은 사람만 위젯 관리에서 내린다.
    fixed: false,
    sizes: ['sm', 'wide'],
    defaultSize: 'sm',
    defaultVisible: true,
    Component: TodoWidget,
  },
  {
    widgetKey: 'notices',
    title: '공지사항',
    description: '최근 공지 5건을 보여줍니다.',
    icon: 'bell',
    // 할일과 같이 고정을 풀었다(사용자 지시 2026-09-23).
    fixed: false,
    sizes: ['sm', 'wide'],
    defaultSize: 'sm',
    defaultVisible: true,
    Component: NoticesWidget,
  },
  {
    widgetKey: 'hr_info',
    title: '인사 정보',
    description: '잔여 연차, 급여명세서, 최근 5개년 인사고과, 자격증·라이센스 갱신 정보를 보여줍니다.',
    icon: 'user',
    fixed: false,
    sizes: ['sm', 'wide'],
    defaultSize: 'sm',
    // 기본은 끈다 — 넣은 사람에게만 보인다(사용자 지시 2026-10-15 · 저장된 배치는 V480 이 한 번 껐다).
    defaultVisible: false,
    Component: HrInfoWidget,
  },
  {
    // 현장 준비(DOC-61 §11 · V176). `fixed: false` 지만 <b>서버가 강제로 켠다</b> —
    // 계약 날인 ~ 착공 사이거나 준공 4개월 앞이고, 그 현장에 관계된 사람일 때만이라, 조건이
    // DB 에 있어 여기서는 알 수 없다(`GET /v1/home/widgets/conditions` → useHomeWidgets 의 동적 고정).
    // `requiresMenuKey` 는 카탈로그를 좁힐 뿐이고, 강제 대상은 그와 별개로 서버가 정한다.
    //
    // `onlyWhenEligible` 로 <b>사람이 고를 수 있는 위젯 목록에서 아예 뺀다</b>(사용자 지시 2026-08-27).
    // 챙길 현장이 없으면 이 위젯은 무조건 빈 칸이라, 위젯 관리에 놔두면 「켜 봤자 아무것도 없는
    // 위젯」 이 목록에 남는다. 조건이 서면 서버가 카탈로그에 들여보내고 동시에 켠다.
    //
    // ★ 키를 `site_launch` 에서 바꿨다(사용자 지시 2026-09-01 — "하나로 현장 위젯같은거로 합쳐서").
    //   착공과 준공을 나란히 두면 홈에 비슷한 네모가 둘 뜨고, 그러면 둘 다 대충 읽힌다.
    //   옛 키로 저장된 배치는 카탈로그에 없어 `mergeSlots` 가 조용히 버리는데, 이 위젯은 조건이
    //   서면 서버가 강제로 켜므로 그 버림으로 잃는 것이 없다.
    //
    // ★ 이름을 「현장 준비」 에서 「현장 알림」 으로 바꿨다(사용자 지시 2026-10-06). 진행 현장의 공정·서류·발주
    //   알림이 같은 칸에 들어오면서 「준비」 가 담는 범위보다 넓어졌다. 키는 그대로 둔다 — 바꾸면 저장된 배치에서 빠진다.
    widgetKey: 'site_prep',
    title: '현장 알림',
    description:
      '착공을 앞둔 현장의 법정인원 공석·소방착공 신고·미비 착공서류, 준공 4개월 앞으로 다가온 현장의 미발주 용역(준공용역·건설지), 진행 현장의 공정·서류·발주·선임 신고·인원 알림을 함께 보여줍니다.',
    icon: 'clipboard',
    fixed: false,
    sizes: ['sm', 'wide'],
    // 반칸으로 시작한다. 배지가 여러 개라 좁으면 줄이 접히지만, 접혀도 읽힌다 —
    // 강제로 켜지는 위젯이 홈 첫 줄을 통째로 차지하면 나머지 위젯이 전부 아래로 밀린다.
    // 넓게 쓰고 싶으면 위젯 관리에서 wide 로 바꾼다.
    defaultSize: 'sm',
    // 기본으로 켜지 않는다 — 챙길 현장이 없으면 빈 칸이다.
    // 있을 때는 서버가 강제로 켜므로, 이 값이 false 라고 알림을 놓치지 않는다.
    defaultVisible: false,
    requiresMenuKey: 'site_status',
    onlyWhenEligible: true,
    Component: SitePrepWidget,
  },
  {
    // 기술인 교육(DOC-61 §6). `hr_info` 에 섞지 않은 이유는 <b>대상이 다르기</b> 때문이다 —
    // 저쪽은 전 직원의 개인 자료이고 이쪽은 기술인만 해당한다. 섞으면 해당 없는 사람의 홈에
    // 늘 빈 칸이 남는다. `requiresMenuKey` 를 걸지 않는 것도 같은 이유다: 이 위젯이 보여 주는
    // 것은 **내 자료**라 관리 권한(hr_tech_education)과 상관없이 본인이 봐야 한다.
    //
    // 대상이 아닌 사람에게는 <b>목록에서도 뺀다</b>(`onlyWhenEligible` · 사용자 지시 2026-08-27).
    // 예전에는 「등록된 기술등급이 없습니다」 라는 빈 칸이 비기술직 홈에 늘 떠 있었다 — 위젯이
    // 「해당 없음」 을 말할 줄 아는 것과, 해당 없는 사람의 홈 한 칸을 차지해도 되는 것은 다르다.
    // 강제는 아니다: 자격이 되면 목록에 나오고 기본으로 켜지되, 끄면 꺼진 채로 남는다.
    widgetKey: 'tech_edu',
    title: '기술인 교육',
    description: '승급교육 미이수, 계속교육 유효기간, 수강 확정된 교육과 교육비 환급 진행을 보여줍니다.',
    icon: 'clipboard',
    fixed: false,
    sizes: ['sm', 'wide'],
    defaultSize: 'sm',
    // 기본으로 켠다. 계속교육 만료와 환급 기한은 <b>놓치면 되돌릴 수 없는</b> 값이고,
    // 켜야 보이는 알림은 결국 아무도 안 켠다(내 현장 대화방과 같은 판단).
    // 「기본으로 켠다」 는 이제 <b>기술인에게만</b> 걸린다(onlyWhenEligible).
    // → 기본은 끈다 — 넣은 사람에게만 보인다(사용자 지시 2026-10-15 · 저장된 배치는 V480 이 한 번 껐다).
    defaultVisible: false,
    onlyWhenEligible: true,
    Component: TechEducationWidget,
  },
  {
    // 공지사항과 나눈 위젯이다(사용자 지시 2026-08-28). 저쪽은 최근 5건을 제목만 흘려보내는 자리라,
    // 생일·경조사가 섞이면 계약·착공 공지에 밀려 그날 안 보인다 — 축하는 그날 못 보면 없던 일이 된다.
    //
    // `fixed` 는 아니다. 공지는 "내가 켰는지" 에 좌우되면 안 되는 정보라 고정이지만, 축하는
    // 그렇게까지 강제할 것이 아니다. 대신 기본으로 켠다 — 켜야 보이는 축하는 아무도 안 켠다.
    widgetKey: 'bomi_news',
    title: '보미소식',
    description: '최근 일주일의 생일자와 경조사를 보여줍니다. 눌러서 공지사항에서 댓글을 남길 수 있습니다. 경조사가 며칠 서 있을지는 글쓴이가 정합니다.',
    icon: 'users',
    fixed: false,
    sizes: ['sm', 'wide'],
    // 이름 몇 줄이 전부라 반칸으로 읽힌다(사용자 지시 — 크게 두지 않는다).
    defaultSize: 'sm',
    defaultVisible: true,
    requiresMenuKey: 'news_notices',
    Component: BomiNewsWidget,
  },
  {
    widgetKey: 'weather',
    title: '날씨',
    description: '내 소속 지역의 강수·바람·특보를 보여줍니다.',
    icon: 'sun',
    fixed: false,
    sizes: ['sm', 'wide'],
    defaultSize: 'sm',
    defaultVisible: true,
    Component: WeatherWidget,
  },
  {
    // 환율(수출입은행 무료 API)과 자재 단가(자산관리에서 손입력). 성격이 달라 한 칸 안에서 선으로 가른다.
    //
    // `requiresMenuKey` 를 걸지 않는다 — 고시 환율도 회사가 정한 단가도 <b>읽는 것은 전 직원</b>이고,
    // 넣는 쪽만 `asset_material_prices` 로 막힌다. 여기에 그 키를 걸면 단가를 보려고 관리 권한을
    // 나눠 주게 된다.
    widgetKey: 'market',
    title: '환율 · 자재 단가',
    description: '미국·일본·중국·유럽 고시 환율과, 자산관리에 등록한 자재 단가를 보여줍니다. 자재 단가를 넣지 않으면 환율만 나옵니다.',
    icon: 'chart',
    fixed: false,
    sizes: ['sm', 'wide'],
    defaultSize: 'sm',
    // 기본으로 켜지 않는다. 환율이 매일 필요한 회사도, 장식인 회사도 있다 — 자재 단가를 넣을
    // 생각이 없는 곳에 환율 네 칸을 홈 첫 화면에 강제하지 않는다(현장별 매출·원가와 같은 판단).
    defaultVisible: false,
    Component: MarketWidget,
  },
  {
    widgetKey: 'meetings_week',
    title: '오늘 회의',
    description: '오늘 잡힌 본사·현장 월간회의 일정을 보여줍니다.',
    icon: 'calendar',
    fixed: false,
    sizes: ['sm', 'wide'],
    defaultSize: 'sm',
    defaultVisible: true,
    requiresMenuKey: 'monthly_hq',
    Component: MeetingsWeekWidget,
  },
  {
    widgetKey: 'weekly_report',
    title: '대표님 전달/지시사항',
    description: '주간회의 대시보드의 본사·현장 대표님 전달/지시사항을 보여줍니다.',
    icon: 'clipboard',
    fixed: false,
    sizes: ['sm', 'wide'],
    defaultSize: 'sm',
    defaultVisible: true,
    requiresMenuKey: 'dashboard',
    Component: WeeklyReportWidget,
  },
  {
    widgetKey: 'site_summary',
    title: '현장 현황',
    description: '전 현장을 전체·사용중·진행·예정·준공으로 집계합니다.',
    icon: 'chart',
    fixed: false,
    sizes: ['sm', 'wide'],
    defaultSize: 'sm',
    defaultVisible: true,
    requiresMenuKey: 'site_status',
    Component: SiteSummaryWidget,
  },
  {
    // 현장으로 바로 들어가는 입구(사용자 지시 2026-10-15). 격자가 아니라 오른쪽 세로 기둥에 선다.
    // 순서는 현장 현황과 같다 — 별표 먼저, 그다음 내 현장, 나머지는 현장 관리 순서.
    widgetKey: 'site_list',
    title: '현장 리스트',
    description: '현장 목록을 국내 진행 · 해외 진행 · 예정 · 평가 · 준공으로 나눠 홈 오른쪽에 세웁니다. 누르면 그 현장의 현장 조회로 들어갑니다.',
    icon: 'building',
    fixed: false,
    sizes: ['sm'],
    defaultSize: 'sm',
    defaultVisible: false,
    requiresMenuKey: 'site_status',
    placement: 'rail',
    Component: SiteListWidget,
  },
  {
    widgetKey: 'office_summary',
    title: '현장별 매출·원가',
    description: '현장별 매출·원가·원가율을 표로 봅니다. 매출 상위 5곳과 합계를 보여주고, 목표원가율을 넘긴 현장은 빨갛게 세웁니다.',
    icon: 'chart',
    fixed: false,
    sizes: ['sm', 'wide'],
    defaultSize: 'sm',
    // 기본으로 켜지 않는다 — 금액이 홈 첫 화면에 늘 떠 있을 자료인지는 현장별로 갈린다.
    // 자료도 아직 각자 브라우저의 초안이라, 서버 집계가 서기 전까지는 켠 사람에게만 보인다.
    defaultVisible: false,
    requiresMenuKey: 'site_status',
    Component: OfficeSummaryWidget,
  },
  {
    widgetKey: 'site_chat_rooms',
    title: '내 현장 대화방',
    description: '내 소속 현장의 카카오톡 오픈채팅방 입구입니다. 누르면 카카오톡으로 연결되며, 대화 내용은 저장되지 않습니다.',
    icon: 'message',
    fixed: false,
    sizes: ['sm', 'wide'],
    defaultSize: 'sm',
    // 기본으로 켠다. "링크 찾으러 카톡 스크롤" 을 없애는 것이 이 기능의 값 대부분이라,
    // 위젯 관리에서 찾아 켜야 한다면 그 값이 대부분 사라진다(DOC-26 §5).
    defaultVisible: true,
    requiresMenuKey: 'site_status',
    Component: SiteChatRoomsWidget,
  },
  {
    // 구 'requests_open'(업무요청 게시판)을 대체한다. 키를 바꾼 이유: 보여주는 자료가 다른 표다.
    // 예전 키로 저장된 배치는 카탈로그에 없어 자연히 버려진다(mergeSlots).
    widgetKey: 'improvement_open',
    title: '미처리 개선사항 요청',
    description: '헤더의 개선사항 요청으로 접수된 건입니다. 전산팀·공사전략팀이 받아 처리합니다.',
    icon: 'message',
    fixed: false,
    sizes: ['sm', 'wide'],
    defaultSize: 'sm',
    // 접수는 전 직원이 하지만 이 위젯은 <b>받는 쪽</b>의 처리 큐다(전산팀·공사전략팀).
    // 기본은 끈다 — 넣은 사람에게만 보인다(사용자 지시 2026-10-15 · 저장된 배치는 V480 이 한 번 껐다).
    defaultVisible: false,
    requiresMenuKey: 'improvement_handle',
    Component: ImprovementRequestsWidget,
  },
  {
    widgetKey: 'employee_news',
    title: '신규입사자 소식',
    description: '이번 달 입사·퇴사 소식입니다.',
    icon: 'users',
    fixed: false,
    sizes: ['sm', 'wide'],
    defaultSize: 'sm',
    defaultVisible: false,
    requiresMenuKey: 'news_employee',
    Component: EmployeeNewsWidget,
  },
  {
    widgetKey: 'dining_today',
    title: '주단위 식단표',
    description: '본사·현장 구내식당의 이번 주(월~일) 식단표입니다. 본사/현장을 토글해 보고, 담당자는 연필로 주차별 식단을 넣습니다.',
    icon: 'food',
    fixed: false,
    sizes: ['sm', 'wide'],
    defaultSize: 'sm',
    defaultVisible: false,
    requiresMenuKey: 'news_dining',
    Component: WeeklyMenuWidget,
  },
  /*
    뉴스 + 건설동향 (사용자 지시 2026-09-18 — 「둘을 한 위젯으로」).

    <p>같은 날 두 메뉴가 사이드바에서 내려갔고, 이제 <b>이 위젯의 전체보기가 유일한 입구</b>다.
    따로 두면 안 됐던 이유는 건설동향 위젯이 <b>기본 꺼짐</b>이었다는 것이다 — 켠 적 없는 사람에게는
    메뉴도 위젯도 없어 들어갈 길이 하나도 안 남는다.

    ★ `widgetKey` 는 `news` 를 <b>그대로 쓴다</b>. 이미 저장된 배치가 이 키를 들고 있고(기본 켜짐),
      키를 새로 만들면 그 줄이 카탈로그에 없어 버려진 뒤 「새 위젯」 으로 맨 뒤에 다시 붙는다 —
      사람이 맞춰 둔 홈 자리가 통째로 흔들린다. 없어지는 것은 `trends` 쪽 줄뿐이고, 그것은
      `mergeSlots` 가 조용히 버린다.
  */
  {
    widgetKey: 'news',
    title: '뉴스·건설동향',
    description:
      '토글로 나눠 봅니다 — 뉴스는 섹션별 최신 기사(제목을 누르면 언론사 원문), 건설동향은 사내에 올라온 시장 동향 글입니다. 두 화면 모두 전체보기로 들어갑니다.',
    icon: 'news',
    fixed: false,
    sizes: ['sm', 'wide'],
    // 제목만 있는 목록이라 반칸으로도 읽힌다. 넓게 쓰고 싶으면 위젯 관리에서 wide 로 바꾼다.
    defaultSize: 'sm',
    // 출근해서 훑는 자리이고, <b>이제는 두 화면의 유일한 문</b>이라 기본으로 켜 둔다.
    defaultVisible: true,
    requiresMenuKey: ['news_headlines', 'news_trends'],
    Component: NewsWidget,
  },
];

const BY_KEY = new Map(WIDGET_DEFINITIONS.map((definition) => [definition.widgetKey, definition] as const));

export function widgetByKey(widgetKey: string): WidgetDefinition | null {
  return BY_KEY.get(widgetKey) ?? null;
}
